import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Package, Plus, Search, Trash2 } from 'lucide-react'

import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useDebouncedCallback } from '@/hooks/use-debounced-callback'
import {
    SearchProductOutputItem,
    useProductDetails,
    useSearchProducts,
} from '@/lib/api/products'
import { cn } from '@/lib/utils'

interface SearchProductListInputProps {
    storeId: string
    selectedProductIds: string[]
    onChange: (productIds: string[]) => void
    placeholder?: string
    includeInactive?: boolean
}

const getProductImage = (product: SearchProductOutputItem): string | null => {
    const defaultVariant = product.variants.find((v) => v.default)
    return defaultVariant?.imageUrl ?? product.variants[0]?.imageUrl ?? null
}

export const SearchProductListInput = ({
    storeId,
    selectedProductIds,
    onChange,
    placeholder,
    includeInactive = false,
}: SearchProductListInputProps) => {
    const { t } = useTranslation('common')
    const [open, setOpen] = React.useState(false)
    const [searchInput, setSearchInput] = React.useState('')
    const [searchValue, setSearchValue] = React.useState<string | null>(null)
    const [draftIds, setDraftIds] = React.useState<string[]>(selectedProductIds)

    const debouncedSetSearch = useDebouncedCallback((value: string) => {
        setSearchValue(value.length > 0 ? value : null)
    }, 250)

    React.useEffect(() => {
        if (open) {
            setDraftIds(selectedProductIds)
            setSearchInput('')
            setSearchValue(null)
        }
    }, [open, selectedProductIds])

    const { data: selectedProducts } = useProductDetails(selectedProductIds, selectedProductIds.length > 0)
    const { data: searchResults, isLoading } = useSearchProducts(
        { storeId, productId: null, searchValue, includeInactive },
        { enabled: open },
    )

    const toggleDraft = (productId: string, checked: boolean) => {
        setDraftIds((prev) => {
            if (checked) return prev.includes(productId) ? prev : [...prev, productId]
            return prev.filter((id) => id !== productId)
        })
    }

    const handleConfirm = () => {
        onChange(draftIds)
        setOpen(false)
    }

    const handleRemove = (productId: string) => {
        onChange(selectedProductIds.filter((id) => id !== productId))
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <div className="space-y-2">
                {selectedProductIds.length > 0 && (
                    <div className="space-y-2 rounded-md border p-2">
                        {selectedProductIds.map((id) => {
                            const product = selectedProducts?.find((p) => p.id === id)
                            const imageUrl = product ? getProductImage(product) : null
                            return (
                                <div key={id} className="flex items-center gap-3">
                                    <div className="bg-muted flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md">
                                        {imageUrl ? (
                                            <img src={imageUrl} alt={product?.name ?? ''} className="size-full object-cover" />
                                        ) : (
                                            <Package className="text-muted-foreground size-4" />
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <p className="truncate text-sm font-medium">{product?.name ?? '…'}</p>
                                            {product?.active === false && (
                                                <Badge variant="secondary">{t('formComponents.inactive')}</Badge>
                                            )}
                                        </div>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="text-muted-foreground hover:text-destructive size-7"
                                        onClick={() => handleRemove(id)}
                                        aria-label={t('delete')}
                                    >
                                        <Trash2 className="size-3.5" />
                                    </Button>
                                </div>
                            )
                        })}
                    </div>
                )}
                <DialogTrigger asChild>
                    <Button type="button" variant="outline" size="sm" className="w-full">
                        <Plus className="mr-1 size-3.5" />
                        {placeholder ?? t('formComponents.addProduct')}
                    </Button>
                </DialogTrigger>
            </div>
            <DialogContent className="flex max-h-[80vh] flex-col gap-0 p-0 sm:max-w-[600px]">
                <DialogHeader className="border-b p-4">
                    <DialogTitle>{t('formComponents.selectProductsTitle')}</DialogTitle>
                </DialogHeader>
                <div className="border-b p-4">
                    <div className="relative">
                        <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                        <Input
                            autoFocus
                            value={searchInput}
                            onChange={(e) => {
                                setSearchInput(e.target.value)
                                debouncedSetSearch(e.target.value)
                            }}
                            placeholder={t('formComponents.searchProduct')}
                            className="pl-10"
                        />
                    </div>
                </div>
                <div className="min-h-[200px] flex-1 overflow-y-auto">
                    {isLoading && (
                        <div className="flex items-center justify-center p-8">
                            <Loader2 className="text-muted-foreground size-5 animate-spin" />
                        </div>
                    )}
                    {!isLoading && (!searchResults || searchResults.length === 0) && (
                        <p className="text-muted-foreground p-8 text-center text-sm">
                            {t('formComponents.noProductsFound')}
                        </p>
                    )}
                    {!isLoading &&
                        searchResults?.map((product) => {
                            const checked = draftIds.includes(product.id)
                            const imageUrl = getProductImage(product)
                            return (
                                <label
                                    key={product.id}
                                    htmlFor={`list-${product.id}`}
                                    className={cn(
                                        'flex cursor-pointer items-center gap-3 border-b p-3 last:border-b-0',
                                        checked ? 'bg-accent/40' : 'hover:bg-accent/20',
                                    )}
                                >
                                    <Checkbox
                                        id={`list-${product.id}`}
                                        checked={checked}
                                        onCheckedChange={(value) => toggleDraft(product.id, value === true)}
                                    />
                                    <div className="bg-muted flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md">
                                        {imageUrl ? (
                                            <img src={imageUrl} alt={product.name} className="size-full object-cover" />
                                        ) : (
                                            <Package className="text-muted-foreground size-5" />
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <p className="truncate text-sm font-medium">{product.name}</p>
                                            {!product.active && (
                                                <Badge variant="secondary">{t('formComponents.inactive')}</Badge>
                                            )}
                                        </div>
                                        {product.description && (
                                            <p className="text-muted-foreground truncate text-xs">{product.description}</p>
                                        )}
                                    </div>
                                    <Label className="text-muted-foreground text-xs">
                                        {t('formComponents.variantsTotal', {
                                            count: product.variants.length,
                                        })}
                                    </Label>
                                </label>
                            )
                        })}
                </div>
                <DialogFooter className="border-t p-4">
                    <Button variant="outline" onClick={() => setOpen(false)}>
                        {t('cancel')}
                    </Button>
                    <Button onClick={handleConfirm}>{t('confirm')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
